import { TrackingService } from '../TrackingService.browser.ts';
import type {
    ResolvedSelectionResponse,
    SelectionDomAdapter,
    SelectionResolver,
    SelectionTrackingAdapter,
} from './SelectionDependencies.ts';

const FILTER_RESOLVER_ENDPOINT = '/grasshopper/filter/resolve/dreson';
const TRACKING_CONTEXT_ID = 'reptile-sfid-wrapper';
const INTEGER_PATTERN = /^(0|[1-9]\d*)$/;

export function createProductionSelectionDependencies(): {
    resolver: SelectionResolver;
    dom: SelectionDomAdapter;
    tracking: SelectionTrackingAdapter;
} {
    const trackingService = new TrackingService();
    return {
        resolver: resolveSelection,
        dom: browserSelectionDom,
        tracking: {
            replaceContext: (url) => trackingService.replaceContext(TRACKING_CONTEXT_ID, url),
        },
    };
}

export const browserSelectionDom: SelectionDomAdapter = {
    bootstrapRule() {
        const rule = document.getElementById('avContent')?.dataset.rule;
        if (!rule) throw new Error('Failed to bootstrap selection: missing rule');
        return rule;
    },
    currentUrl: () => window.location.href,
    replaceHtml(html) {
        const wrapper = document.getElementById(TRACKING_CONTEXT_ID);
        if (!wrapper) throw new Error(`Failed to apply resolved selection: missing #${TRACKING_CONTEXT_ID}`);
        wrapper.innerHTML = html;
    },
    currentSsid() {
        const tsBct = document.getElementById(TRACKING_CONTEXT_ID)?.querySelector<HTMLElement>('.ts-bct');
        return tsBct?.dataset.ts_sfid;
    },
};

export async function resolveSelection(params: URLSearchParams): Promise<ResolvedSelectionResponse> {
    const response = await fetch(`${FILTER_RESOLVER_ENDPOINT}?${params.toString()}`, {
        headers: { 'X-Find-Trackable': 'ts_Assert=none' },
    });
    if (response.status !== 200) {
        throw new Error(`Failed to resolve rule: ${response.status} ${response.statusText}`);
    }
    const html = await response.text();
    return { html, ...parseResolverResponse(html) };
}

export function parseResolverResponse(html: string): Omit<ResolvedSelectionResponse, 'html'> {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector<HTMLElement>('#avContent');
    const rule = element?.dataset.rule;
    const resolvedUrl = element?.dataset.redirect;
    if (!rule || !resolvedUrl) throw new Error('Failed to resolve rule: missing url or rule');

    const rawCount = element.dataset.count;
    let count: number | undefined;
    if (rawCount !== undefined && rawCount.length > 0) {
        if (!INTEGER_PATTERN.test(rawCount)) throw new Error('Failed to resolve rule: invalid count');
        count = Number(rawCount);
        if (!Number.isSafeInteger(count)) throw new Error('Failed to resolve rule: invalid count');
    }

    let url: URL;
    try {
        url = new URL(resolvedUrl, window.location.origin);
    } catch {
        throw new Error('Failed to resolve rule: invalid url');
    }
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.origin !== window.location.origin) {
        throw new Error('Failed to resolve rule: invalid url');
    }
    return { rule, url: url.href, ...(count !== undefined ? { count } : {}) };
}
